import { Text } from '@magicschool/spellbook/components/Text';
import type { CartesianTickItem } from 'recharts/types/util/types';

/** Props passed by Recharts to custom axis tick components */
export interface AxisTickProps {
  /** X coordinate of the tick */
  x: number;
  /** Y coordinate of the tick */
  y: number;
  /** Total height of the axis */
  height: number;
  /** Number of visible ticks on the axis */
  visibleTicksCount: number;
  /** The tick data containing the label value */
  payload: CartesianTickItem;
  /** Index of this tick in the data array */
  index: number;
  /** The chart data array (passed from BarChart) */
  data: Array<{ name?: string; subLabel?: string; [key: string]: unknown }>;
}

/**
 * Custom Y-axis tick component that truncates labels to fit within available width.
 * Uses CSS text-overflow ellipsis to handle long labels gracefully.
 * Supports optional two-line labels with subLabel.
 */
export const TruncatedYAxisTick = ({ x, y, payload, height, visibleTicksCount, index, data }: AxisTickProps) => {
  if (visibleTicksCount === 0) return null;

  const dataItem = data[index];
  const name = dataItem?.name ? String(dataItem.name) : String(payload.value);
  const subLabel = dataItem?.subLabel ? String(dataItem.subLabel) : undefined;

  const tickHeight = height / visibleTicksCount;
  const baseLabelHeight = subLabel ? 40 : 20;
  const labelHeight = Math.min(tickHeight, baseLabelHeight);
  // Generous width for Y-axis labels - will be constrained by YAxis width prop
  const labelWidth = 280;

  return (
    <foreignObject x={x - labelWidth - 10} y={y - labelHeight / 2} width={labelWidth} height={labelHeight}>
      <div className="flex flex-col items-end justify-center h-full">
        <Text variant="caption-1" className="truncate block text-right text-bar-chart-axis-tick-label" title={name}>
          {name}
        </Text>
        {subLabel && (
          <Text variant="caption-2" className="truncate block text-right text-bar-chart-axis-tick-label" title={subLabel}>
            {subLabel}
          </Text>
        )}
      </div>
    </foreignObject>
  );
};
